import React from 'react';
import { ConversationRole, VOICE_TURN_SEPARATOR } from '@evaltick/shared';
import styles from './FeedbackThread.module.scss';
import type { ConversationEntry } from '@/store/interviewStore';

interface FeedbackThreadProps {
  entries: ConversationEntry[];
  /** When false, the submitted answer renders like the agent's own text (full width, no bubble) rather than a candidate chat bubble — there's no coach reply to visually distinguish it from. */
  coachingEnabled?: boolean;
}

const formatEntryTime = (occurredAtMs?: number): string =>
  occurredAtMs
    ? new Date(occurredAtMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : 'Just now';

// Finalized turns carry a clientTurnId; a still-streaming one doesn't yet, so
// it falls back to its array position — stable for its lifetime since a live
// entry is only ever updated in place, never moved or reordered.
const entryKey = (entry: ConversationEntry, index: number): string => entry.clientTurnId ?? `live${index}`;

const TypingDots: React.FC = () => (
  <div className={styles.typingDots}>
    <span />
    <span />
    <span />
  </div>
);

// Array order *is* chronological order — both the store (text turns) and
// useRealtimeVoice (voice turns) only ever append a new entry at the end and
// update an existing one in place, never insert or reorder. Nothing here
// re-sorts by timestamp on top of that.
export const FeedbackThread: React.FC<FeedbackThreadProps> = ({ entries, coachingEnabled = true }) => {
  if (entries.length === 0) return null;

  // The submitted answer (text or voice) is whatever leading run of Candidate
  // turns precedes the coach's first reply — voice can split it across
  // several transcript turns, text is always exactly one.
  let splitIndex = 0;
  while (splitIndex < entries.length && entries[splitIndex].role === ConversationRole.Candidate) {
    splitIndex++;
  }
  const answerEntries = entries.slice(0, splitIndex);
  const rationale = entries[splitIndex];
  const rest = entries.slice(splitIndex + 1);

  const answerText = answerEntries
    .map((e) => e.content)
    .join(VOICE_TURN_SEPARATOR)
    .trim();
  const rationaleLoading = rationale?.isStreaming && rationale.content === '';

  const answerOccurredAtMs = answerEntries[answerEntries.length - 1]?.occurredAtMs;

  return (
    <div className={styles.container}>
      {answerText &&
        (coachingEnabled ? (
          <div className={styles.userBubble}>
            <div className={styles.userBubbleContent}>
              <p className={styles.userBubbleText}>{answerText}</p>
            </div>
            <div className={styles.msgMeta}>{formatEntryTime(answerOccurredAtMs)}</div>
          </div>
        ) : (
          <div className={styles.feedbackText}>{answerText}</div>
        ))}

      {rationale && (
        <>
          <div className={styles.divider}>
            <span className={styles.line} />
            <span className={styles.pill}>Feedback</span>
            <span className={styles.line} />
          </div>

          {rationaleLoading ? (
            <TypingDots />
          ) : (
            <>
              <p className={styles.feedbackText}>{rationale.content}</p>
              <div className={styles.msgMeta}>{formatEntryTime(rationale.occurredAtMs)}</div>
            </>
          )}
        </>
      )}

      {rest.length > 0 && (
        <div className={styles.thread}>
          <div className={styles.threadScroll}>
            {rest.map((entry, i) => {
              if (entry.role === ConversationRole.Candidate) {
                return (
                  <div key={entryKey(entry, i)} className={styles.userBubble}>
                    <div className={styles.userBubbleContent}>
                      <p className={styles.userBubbleText}>{entry.content}</p>
                    </div>
                    <div className={styles.msgMeta}>{formatEntryTime(entry.occurredAtMs)}</div>
                  </div>
                );
              }
              const loading = entry.isStreaming && entry.content === '';
              return (
                <div key={entryKey(entry, i)} className={styles.aiMsg}>
                  {loading ? (
                    <TypingDots />
                  ) : (
                    <>
                      <p className={styles.aiMsgText}>{entry.content}</p>
                      <div className={styles.msgMeta}>{formatEntryTime(entry.occurredAtMs)}</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
